"use client";
import { Fragment, useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";

import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import Label from "@stadium/ui/src/components/form/Label";
import Button from "@stadium/checkout/src/components/shared/buttons";

import { USE_CARD_OPTIONS } from "@stadium/checkout/src/constants/cards";

import { useCheckoutContext } from "@stadium/checkout/src/contexts/checkout";
import CardCreate from "@stadium/checkout/src/modals/add-credit-card";

import Card from "@stadium/checkout/src/components/payment-options/cards/card";
import styles from "./main.module.scss";

const Cards = ({
  creditCards = [],
  refetchCreditCards,
  defaultValues = {},
  isExistingOrder = false,
  className = "",
  addCreditCardSectionClassName = "",
  isCreditCardsLoading = false,
}) => {
  const { openNewCardFormByDefault: initNewCardFormOpenByDefault = true } = defaultValues;

  const [isNewCardFormOpen, setIsNewCardFormOpen] = useState(false);

  const {
    creditCardId,
    setCreditCardId,
    billingType,
    setBillingType,
    isSingleSourcePaymentMethodSelected,
    isTeamWalletSelectedByDefault,
  } = useCheckoutContext();

  const { creditCardId: creditCard, newCreditCard } = USE_CARD_OPTIONS;

  const userCreditCards = useMemo(() => {
    if (isEmpty(creditCards)) return [];
    return orderBy(creditCards, ["default"], ["desc"]);
  }, [creditCards]);

  const isExistingOrderWithSingleSourcePaymentMethod =
    (isSingleSourcePaymentMethodSelected || isTeamWalletSelectedByDefault) && isExistingOrder;

  useEffect(() => {
    if (
      isEmpty(userCreditCards) &&
      !isCreditCardsLoading &&
      initNewCardFormOpenByDefault &&
      !isNewCardFormOpen
    )
      setIsNewCardFormOpen(true);
  }, [isCreditCardsLoading]); //added only isCreditCardsLoading so that this doesn't get triggered every time order total (initNewCardFormOpenByDefault) changes.

  useEffect(() => {
    if (
      creditCardId &&
      userCreditCards.some((card) => card.isExpired && +card.id === +creditCardId)
    ) {
      setBillingType(null);
      setCreditCardId(null);
    }
  }, [creditCardId, userCreditCards]);

  useEffect(() => {
    if (creditCardId) setBillingType(creditCard.key);
  }, [creditCardId]);

  const selectCreditCard = (id) => {
    setBillingType(creditCard.key);
    setCreditCardId(id);
    setIsNewCardFormOpen(false);
  };

  const handleAddCreditCardSuccess = (card) => {
    refetchCreditCards();
    selectCreditCard(card.id);
  };

  const renderCreditCardForm = (addCreditCardSec) => (
    <article className={addCreditCardSec}>
      <div className={styles.cardSectionWrap}>
        <CardCreate
          data={{
            onSuccess: handleAddCreditCardSuccess,
            submitBtn: {
              text: "ADD",
              classes: `btn-add-credit-card`,
            },
          }}
        />
      </div>
    </article>
  );

  const handleClick = () => {
    if (billingType === newCreditCard.key || isNewCardFormOpen) {
      setBillingType(creditCard.key);
      setIsNewCardFormOpen(false);
    } else {
      setBillingType(newCreditCard.key);
      setIsNewCardFormOpen(true);
    }
  };

  const renderNewCardOption = () => {
    if (isExistingOrderWithSingleSourcePaymentMethod) return null;

    return (
      <>
        <Button
          styleType="link"
          className={`${styles.addCardButton} ${
            isNewCardFormOpen ? styles.addCardSelected : styles.collapseCard
          }`}
          onClick={handleClick}
        >
          <span
            className={`${isNewCardFormOpen ? styles.subtractCardIcon : styles.addCardIcon} `}
          />
          Add Debit or Credit Card
        </Button>
        {isNewCardFormOpen && renderCreditCardForm(addCreditCardSectionClassName)}
      </>
    );
  };

  const renderCreditCards = () => {
    return (
      <>
        {userCreditCards.map((card) => {
          const { id, isExpired } = card;
          const isActive = Number(creditCardId) === Number(id);
          return (
            <Fragment key={id}>
              <RadioInput
                id={id}
                name={card}
                checked={isActive}
                disabled={isExistingOrderWithSingleSourcePaymentMethod || isExpired}
                onClick={() => setCreditCardId(id)}
                label={
                  <Label iconType="radioOutline" styles={styles} className={styles.inputLabel}>
                    <Card card={card} />
                  </Label>
                }
              />
              <hr className={styles.divider} />
            </Fragment>
          );
        })}
      </>
    );
  };

  return (
    <div className={`${styles.cardContainer} ${className}`}>
      {userCreditCards.length > 0 && renderCreditCards()}
      {renderNewCardOption()}
    </div>
  );
};

export default Cards;
